import { FORM_BIT, GEAR_SLOTS, INCURSION_OMENS, uniqueFor, type UniqueId, type HeroClass, type IncursionOmen, type StructureType } from '@somnhold/content';
import { createBastionMap, createBattle, generateWave, omenRules, type BattleRules, type BattleState } from '@somnhold/sim';

/** Phase 1 practice field: one sector, gate and path; warrior vs shadow goblins; instant building with a purse. */
export const PRACTICE_BUILDS: StructureType[] = ['ballista', 'catapult', 'arcane'];

/** Development scenarios (`?practice&demo=mix|boss|era1|era2|era3|era4|era5&cls=…`): every nightmare type, the witch's wave, or an era's new creatures. */
export interface PracticeDemo {
  demo?: 'mix' | 'boss' | 'era1' | 'era2' | 'era3' | 'era4' | 'era5' | 'forms';
  cls?: HeroClass;
  /** The hero wears its class's three uniques awakened to this stage, to look at them (`&awaken=4`). */
  awaken?: number;
  /** A day's omen over the field, to look at it (`&omen=storm`). */
  omen?: IncursionOmen;
  /** With `awaken` 2 or more, every piece carried in this form (`&forms=a|b`; the final one too from stage 4). */
  forms?: 'a' | 'b';
  /** Spectres of these classes fighting beside the hero, dressed the same (`&party=mage,druid`): the worst fight to look at. */
  party?: HeroClass[];
}

/** A hero's three unique pieces as the practice dresses them (`awaken`, `forms`). */
const dressed = (cls: HeroClass, opts: PracticeDemo): { gear?: { uniques: UniqueId[]; uniqueLv: number[] }; forms?: Partial<Record<UniqueId, number>> } => {
  if (!opts.awaken) return {};
  const uniques = GEAR_SLOTS.map((slot) => uniqueFor(cls, slot).id);
  const bits = (opts.awaken >= 2 && opts.forms ? FORM_BIT[opts.forms] : 0) | (opts.awaken >= 4 ? FORM_BIT.final : 0);
  return { gear: { uniques, uniqueLv: GEAR_SLOTS.map(() => opts.awaken as number) }, ...(bits ? { forms: Object.fromEntries(uniques.map((id) => [id, bits])) } : {}) };
};

/** The omen's rules as an incursion of that day would carry them. */
const omenOf = (id: IncursionOmen): BattleRules => {
  const o = INCURSION_OMENS[id];
  return omenRules({ omen: id, ...(o.enemySpeedBp ? { enemySpeedBp: o.enemySpeedBp } : {}), ...(o.enemyHpBp ? { enemyHpBp: o.enemyHpBp } : {}), ...(o.heroRangeBp ? { heroRangeBp: o.heroRangeBp } : {}) }, o);
};

export const createPractice = (seed: number, name: string, opts: PracticeDemo = {}): BattleState => {
  const map = createBastionMap(seed, 1);
  const demoWave = opts.demo
    ? generateWave({
        seed,
        marea: opts.demo === 'boss' ? 2 : 1,
        wave: opts.demo === 'boss' ? 5 : 2,
        sectors: 1,
        activityBp: 10000,
        depth: 'light',
        lossPenalty: false,
        // The unique forms' worst fight (tools/perf/forms-fight.mjs): a thick wave of small and big ones for a party at 30.
        ...(opts.demo === 'forms' ? { weights: { goblin: 4, wolf: 2, skeleton: 2, wraith: 1, giant: 1, troll: 1 }, budgetOverride: 2600, spawnWindow: 60 * 12 } : {}),
        ...(opts.demo === 'mix' ? { weights: { goblin: 1, wolf: 1, skeleton: 1, wraith: 1, gargoyle: 1, slime: 1, stalker: 1 } } : {}),
        ...(opts.demo === 'era1' ? { weights: { goblin: 2, slinger: 2, crow: 2, mourner: 2, ogre: 1 }, budgetOverride: 260 } : {}),
        ...(opts.demo === 'era2' ? { weights: { goblin: 3, troll: 1, beetle: 1, bannerman: 1, bonesinger: 1, wyvern: 1 }, budgetOverride: 320 } : {}),
        ...(opts.demo === 'era3' ? { weights: { goblin: 3, giant: 1, moth: 2, weaver: 1, oracle: 1, wyrmling: 1 }, budgetOverride: 420 } : {}),
        ...(opts.demo === 'era4' ? { weights: { goblin: 3, sentinel: 2, frostColossus: 1, stormHerald: 1, shellSerpent: 1 }, budgetOverride: 520 } : {}),
        ...(opts.demo === 'era5' ? { weights: { goblin: 3, glassDrake: 1, shellback: 1, lidlessDreamer: 1, ashWatcher: 2 }, budgetOverride: 700 } : {}),
      })
    : null;
  const wave = demoWave ?? generateWave({
    seed,
    marea: 0,
    wave: 3,
    sectors: 1,
    activityBp: 10000,
    depth: 'light',
    lossPenalty: false,
    weights: { goblin: 1 },
    // 360 until the warrior's cleave (docs/BALANCE_CLASES.md §7): with it a warrior standing still at the gate held the field alone.
    budgetOverride: 420,
    spawnWindow: 150 * 12,
    lateInkBp: 3500,
  });
  const gate = map.sectors[0]!.gate;
  return createBattle({
    mode: 'practice',
    seed,
    map,
    // The forms' fight: no gate, so the wave walks into the party.
    structures: opts.demo === 'forms' ? [] : [{ type: 'gate', level: 1, material: 1, q: gate.q, r: gate.r }],
    heroes: [
      {
        pid: 'me',
        name,
        cls: opts.cls ?? 'warrior',
        level: opts.demo === 'forms' ? 30 : opts.demo ? 6 : 1,
        control: 'live',
        powerBp: 10000,
        post: 0,
        ...dressed(opts.cls ?? 'warrior', opts),
      },
      ...(opts.party ?? []).map((cls, i) => ({ pid: `party:${i}`, name: cls, cls, level: opts.demo === 'forms' ? 30 : opts.demo ? 6 : 1, control: 'ghost' as const, powerBp: 10000, post: 0, ...dressed(cls, opts) })),
    ],
    keepHp: opts.demo === 'forms' ? 60000 : 2500,
    keepMaxHp: opts.demo === 'forms' ? 60000 : 2500,
    marea: 0,
    wave: 0,
    spawns: wave.spawns,
    ...(opts.omen ? { rules: omenOf(opts.omen) } : {}),
    purse: opts.demo ? { wood: 600, stone: 500, crystal: 300, dust: 0 } : { wood: 200, stone: 120, crystal: 60, dust: 0 },
  });
};
